import React from "react";
import MarketingLayout, { SpaLink } from "./MarketingLayout";
import VideoEmbed from "./VideoEmbed";
import TryDemoCta from "./TryDemoCta";

const VIDEO_ID = "157UDc_HvSE";
const VIDEO_TITLE = "How GearCheckAuto works for vehicle owners";
const VIDEO_DESCRIPTION =
  "A walkthrough of GearCheckAuto for vehicle owners: describe what your car needs, send one request to several nearby repair shops and mobile mechanics, then compare quotes and response times and book the best fit.";

// The video's upload date, in full ISO 8601 with a timezone offset. Required by Google for video
// rich results, so keep it accurate if the video is ever re-uploaded.
//
// A bare "YYYY-MM-DD" parses as valid but Search Console flags it ("Datetime property uploadDate
// is missing a timezone") and it is not eligible for the full set of video features. This exact
// string is what YouTube itself publishes for the video - see its own itemprop="uploadDate" meta
// tag - so the two sources agree rather than disagreeing by a timezone.
const VIDEO_UPLOAD_DATE = "2026-07-25T12:29:21-07:00";

const JSON_LD = [
  {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: VIDEO_TITLE,
    description: VIDEO_DESCRIPTION,
    thumbnailUrl: [`https://i.ytimg.com/vi/${VIDEO_ID}/maxresdefault.jpg`],
    embedUrl: `https://www.youtube.com/embed/${VIDEO_ID}`,
    contentUrl: `https://www.youtube.com/watch?v=${VIDEO_ID}`,
    ...(VIDEO_UPLOAD_DATE ? { uploadDate: VIDEO_UPLOAD_DATE } : {}),
    publisher: {
      "@type": "Organization",
      name: "GearCheckAuto",
      url: "https://gearcheckauto.com/",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://gearcheckauto.com/" },
      { "@type": "ListItem", position: 2, name: "How it works", item: "https://gearcheckauto.com/how-it-works" },
    ],
  },
];

const STEPS = [
  {
    n: "1",
    title: "Describe what your car needs",
    body: [
      "Pick your vehicle's year, make, and model, then describe the problem in plain language - a noise, a warning light, a service that is due, or an inspection before you buy a used car.",
      "You can attach photos or a short video, which usually saves a round of back-and-forth. Enter your ZIP code and choose how far you are willing to travel.",
    ],
  },
  {
    n: "2",
    title: "Pick nearby shops and send once",
    body: [
      "You get a list and a map of nearby shops and mobile mechanics, with their specialties, services, Google rating, typical response time, and completed work history.",
      "Select several shops and send the same request to all of them at once. No calling around, no repeating yourself. Mobile mechanics show their service area, so you can find someone who comes to you.",
    ],
  },
  {
    n: "3",
    title: "Compare replies and book",
    body: [
      "Responses come back into one inbox, so you can compare quotes and answers side by side instead of juggling phone calls and voicemails.",
      "When you pick a shop, the request becomes a work order you can both follow - quoted, scheduled, in progress, completed - so nothing gets lost after the first message.",
    ],
  },
];

const FAQS = [
  {
    q: "Does it cost anything?",
    a: "GearCheckAuto is free for vehicle owners. You are never obligated to book with any shop that responds.",
  },
  {
    q: "How many shops should I contact?",
    a: "Two to four is the sweet spot. One reply gives you nothing to compare, and comparing a few quotes is where most of the value is.",
  },
  {
    q: "What if there are no shops near me?",
    a: "Widen the search radius, or include general repair shops rather than only specialists for your make. The search suggests whichever of those will actually turn up more results.",
  },
  {
    q: "Can a mechanic come to me?",
    a: "Yes. Mobile mechanics travel to you and list the area they cover, so you can request one instead of driving to a shop.",
  },
];

export default function HowItWorksPage({ onNavigate, onTryDemo }) {
  return (
    <MarketingLayout onNavigate={onNavigate} jsonLd={JSON_LD}>
      {/* Hero */}
      <section className="bg-gradient-to-b from-blue-50 dark:from-blue-950/40 to-white dark:to-gray-800 px-4 py-16">
        <div className="mx-auto max-w-3xl">
          <p className="text-sm font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">How it works</p>
          <h1 className="mt-3 text-4xl font-bold leading-tight tracking-tight text-slate-900 dark:text-gray-100 md:text-5xl">
            How GearCheckAuto works
          </h1>
          <p className="mt-5 text-lg leading-relaxed text-slate-600 dark:text-gray-300">
            Describe your car's problem once, send that single request to several nearby repair shops
            and mobile mechanics, then compare their quotes and response times and book the one that
            fits. Here is the whole thing in a couple of minutes.
          </p>
        </div>
      </section>

      {/* Demo video */}
      <section className="px-4 pb-4">
        <div className="mx-auto max-w-3xl">
          <VideoEmbed videoId={VIDEO_ID} title={VIDEO_TITLE} />
          <p className="mt-3 text-sm text-slate-500 dark:text-gray-400">
            A walkthrough of sending a request and comparing responses, from a vehicle owner's view.
          </p>
          {/* Highest-intent spot on the site: they have just watched the walkthrough. */}
          {onTryDemo ? <TryDemoCta onTryDemo={onTryDemo} className="mt-6" /> : null}
        </div>
      </section>

      {/* Written walkthrough */}
      <section className="px-4 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-gray-100">
            Three steps, start to finish
          </h2>
          <div className="mt-10 space-y-10">
            {STEPS.map((step) => (
              <div key={step.n} className="flex gap-5">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-700 text-base font-bold text-white">
                  {step.n}
                </span>
                <div>
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-gray-100">{step.title}</h3>
                  {step.body.map((paragraph) => (
                    <p key={paragraph} className="mt-3 leading-relaxed text-slate-600 dark:text-gray-300">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What you need */}
      <section className="bg-slate-50 dark:bg-gray-900 px-4 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-gray-100">
            What you need to get started
          </h2>
          <ul className="mt-6 space-y-3 text-slate-600 dark:text-gray-300">
            <li>Your vehicle's year, make, and model.</li>
            <li>A short description of the problem or the service you want.</li>
            <li>Your ZIP code, so shops can be matched by distance.</li>
            <li>Optional: photos or a short video of the problem, which helps shops quote accurately.</li>
          </ul>
          <p className="mt-6 leading-relaxed text-slate-600 dark:text-gray-300">
            That is it. Creating an account takes a minute, and it is free for vehicle owners.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-4 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-gray-100">Common questions</h2>
          <dl className="mt-8 space-y-8">
            {FAQS.map((item) => (
              <div key={item.q}>
                <dt className="text-lg font-semibold text-slate-900 dark:text-gray-100">{item.q}</dt>
                <dd className="mt-2 leading-relaxed text-slate-600 dark:text-gray-300">{item.a}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-8 text-slate-600 dark:text-gray-300">
            More answers are on the{" "}
            <SpaLink view="faq" onNavigate={onNavigate} className="font-semibold text-blue-700 dark:text-blue-300 hover:underline">
              FAQ page
            </SpaLink>
            .
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-50 dark:bg-gray-900 px-4 py-16">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-gray-100">
            Ready to send your first request?
          </h2>
          <p className="mt-3 leading-relaxed text-slate-600 dark:text-gray-300">
            Free for vehicle owners. No obligation, and real shops near you.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={() => onNavigate("signup")}
              className="rounded-lg bg-red-600 px-7 py-3 text-base font-semibold text-white shadow-sm hover:bg-red-500"
            >
              Find a Shop
            </button>
            <SpaLink
              view="autoRepair"
              onNavigate={onNavigate}
              className="rounded-lg border border-slate-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-7 py-3 text-base font-semibold text-blue-700 dark:text-blue-300 hover:bg-slate-50 dark:hover:bg-gray-700"
            >
              Auto repair help
            </SpaLink>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
